import { LoadingSpinner } from '@momentum-ui/react-collaboration';
import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  formatDate,
  formatDateDDMMYYYY,
  formatTime,
  formatTimeToSupport24Hours,
} from '../../utils/dateUtils';
import { VoicemailTranscriptKind } from '../../utils/voicemailTranscript';

export interface IVoicemailTranscriptPresentation {
  transcript?: string;
  transcriptKind?: VoicemailTranscriptKind;
  transcriptLoading: boolean;
  transcriptEnabled: boolean;
  onRequestTranscript: () => void;
}

export type VoicemailTranscriptHeadingLevel = 2 | 3 | 4;

export interface IVoicemailTranscriptPanelProps {
  transcript?: string;
  transcriptKind?: VoicemailTranscriptKind;
  loading: boolean;
  date: string;
  isLocaleGerman?: boolean;
  headingLevel?: VoicemailTranscriptHeadingLevel;
  collapsible?: boolean;
}

export const VoicemailTranscriptPanel = ({
  transcript,
  transcriptKind = 'content',
  loading,
  date,
  isLocaleGerman = false,
  headingLevel = 4,
  collapsible = false,
}: IVoicemailTranscriptPanelProps): JSX.Element => {
  const { t } = useTranslation('WebexVoicemail');
  const headingId = useId();
  const Heading = `h${headingLevel}` as 'h2' | 'h3' | 'h4';
  const textRef = useRef<HTMLParagraphElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const statusMessage =
    !loading && transcript && transcriptKind === 'status'
      ? transcript
      : undefined;
  const transcriptBody =
    !loading && transcript && transcriptKind === 'content'
      ? transcript
      : undefined;

  useEffect(() => {
    setExpanded(false);
  }, [transcriptBody]);

  useLayoutEffect(() => {
    if (!collapsible || !transcriptBody || expanded) {
      return;
    }

    const node = textRef.current;
    if (!node) {
      setIsOverflowing(false);
      return;
    }

    const measured = node.scrollHeight > node.clientHeight + 1;
    const estimated =
      transcriptBody.length > 220 ||
      (transcriptBody.match(/\n/g)?.length ?? 0) >= 4;
    setIsOverflowing(measured || estimated);
  }, [collapsible, transcriptBody, expanded]);

  const isClamped = Boolean(collapsible && transcriptBody && !expanded);
  const showMore = Boolean(isClamped && isOverflowing);

  return (
    <section className="voicemail-transcript" aria-labelledby={headingId}>
      <div className="voicemail-transcript__date">
        <span>
          {isLocaleGerman ? formatDateDDMMYYYY(date) : formatDate(date)}
        </span>
        <span aria-hidden="true">·</span>
        <span>
          {isLocaleGerman
            ? formatTimeToSupport24Hours(date)
            : formatTime(date)}
        </span>
      </div>
      <Heading id={headingId}>{t('vmTranscription.transcription')}</Heading>
      {/*
        The live region stays mounted for the whole panel lifetime and carries
        only the loading indicator and short lifecycle messages. Transcript
        bodies are rendered as a sibling so an atomic announcement never reads
        the whole voicemail back to the user.
      */}
      <div role="status" aria-live="polite" aria-atomic="true">
        {loading && <LoadingSpinner scale={28} data-testid="loading-icon" />}
        {statusMessage && (
          <p className="voicemail-transcript__text--centered">
            {statusMessage}
          </p>
        )}
      </div>
      {transcriptBody && (
        <div className="voicemail-transcript__body">
          <p
            ref={textRef}
            className={`voicemail-transcript__text${
              isClamped ? ' voicemail-transcript__text--clamped' : ''
            }`}
          >
            {transcriptBody}
          </p>
          {showMore && (
            <button
              type="button"
              className="voicemail-transcript__more"
              aria-expanded={false}
              onClick={() => setExpanded(true)}
            >
              {t('mobile.transcriptionMore', { defaultValue: 'More' })}
            </button>
          )}
        </div>
      )}
    </section>
  );
};
